import AgeLabel from '@monorepo/common/component/AgeLabel';
import { AppConfigContext } from '@monorepo/common/component/AppConfigProvider';
import ClientNameLabel from '@monorepo/common/component/client/ClientNameLabel';
import DateLabel from '@monorepo/common/component/DateLabel';
import { IconSolidUserPlus } from '@monorepo/common/component/Icons';
import SexLabel from '@monorepo/common/component/SexLabel';
import { RequiredAndNotNull } from '@monorepo/common/type/Types';
import PaymentTokenList from 'component/payment/token/PaymentTokenList';
import React, { ReactNode, useContext } from 'react';
import { Col, ColProps, Row } from 'reactstrap';
import { PersonQuery } from '../../graphql-operations';
import CovidAccountBalance from '../covid/CovidAccountBalance';
import PaymentDisciplineForClient from '../payment/discipline/PaymentDisciplineForClient';
import PaymentStampList from '../payment/stamp/PaymentStampList';
import { ClientDetailNewFamilyModal } from '../register/ClientDetailNewFamilyModal';
import EmailLabel from '@monorepo/common/component/EmailLabel';
import PhoneLabel from '@monorepo/common/component/PhoneLabel';

export type ClientType = RequiredAndNotNull<NonNullable<PersonQuery['person']>, 'clientData'>;

export function NewClientIcon() {
  return (
    <ClientDetailNewFamilyModal title="Nový člen">
      {(handleModalOpen, title) => (
        <h2>
          <IconSolidUserPlus onClick={handleModalOpen} title={title} className="action-icon text-primary" />
        </h2>
      )}
    </ClientDetailNewFamilyModal>
  );
}

interface ModuleColProps extends ColProps {}

/** Adds margin-top */
export const ModuleCol = ({ children, ...props }: ModuleColProps) => (
  <Col {...props} className="mt-4">
    {children}
  </Col>
);

interface ClientRowProps {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
}

function ClientRow({ label, value, children }: ClientRowProps) {
  return (
    <Row>
      <Col xs={5}>
        <span className="text-muted">{label}</span>
      </Col>
      <Col xs={7}>{children || value}</Col>
    </Row>
  );
}

function ClientDoubleRow({ label, value, children }: ClientRowProps) {
  return (
    <Row>
      <Col xs={12}>
        <span className="text-muted">{label}</span>
      </Col>
      <Col xs={12}>{children || value}</Col>
    </Row>
  );
}

interface ClientDetailViewProps {
  client: ClientType;
}

export default function ClientDetailView({ client }: ClientDetailViewProps) {
  const { personData, clientData } = client;

  const { currentPeriod } = useContext(AppConfigContext);

  if (!currentPeriod) {
    return null;
  }

  return (
    <>
      {/* header with name and New icon */}
      <Row>
        <Col>
          <h2>
            <ClientNameLabel client={client} />{' '}
            <span className="text-muted">
              (<AgeLabel age={clientData.age} />)
            </span>
          </h2>
        </Col>
      </Row>

      {/* client info */}
      <Row>
        <Col sm={6}>
          <ClientRow label="Rodné číslo" value={clientData.birthNumber} />
          <ClientRow label="Ulice" value={`${clientData.street} ${clientData.streetNumberJoined}`} />
          <ClientRow label="Město" value={clientData.city} />
          <ClientRow label="PSČ" value={clientData.zip} />
          <ClientRow label="Občanství" value={clientData.citizenship.name} />
        </Col>
        <Col sm={6}>
          {/*@ts-ignore*/}
          <ClientRow label="Pohlaví">{personData.sex && <SexLabel sex={personData.sex} />}</ClientRow>
          <ClientRow label="Datum narození">
            <DateLabel dateString={clientData.birthday} />
          </ClientRow>

          <hr />

          <ClientRow label="Telefon">
            <PhoneLabel phoneNumber={personData.phone} />
          </ClientRow>
          <ClientDoubleRow label="E-mail">
            <EmailLabel email={personData.email} />
          </ClientDoubleRow>
        </Col>
      </Row>

      {/* Modules */}
      <Row className="mb-3">
        <ModuleCol xl={8}>
          <PaymentDisciplineForClient
            key={client.id}
            clientId={client.id}
            paymentsDiscipline={clientData.paymentsDiscipline}
          />
        </ModuleCol>

        <ModuleCol xl={4}>
          <PaymentStampList key={client.id} paymentsStamp={clientData.paymentsStamp} clientId={client.id} />
        </ModuleCol>

        {clientData.covidAccountBalanceInfo.covidAccountBalance > 0 && (
          <ModuleCol sm={12}>
            <CovidAccountBalance key={client.id} covidAccountBalanceInfo={clientData.covidAccountBalanceInfo} />
          </ModuleCol>
        )}

        <ModuleCol xl={6}>
          <PaymentTokenList paymentsToken={clientData.paymentsToken} clientId={client.id} />
        </ModuleCol>
      </Row>
    </>
  );
}
